/*
 * @Description
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpParams,
} from '@angular/common/http';
import {
  Observable,
  of,
} from 'rxjs';
import {
  AvailableDocument,
  Incident,
  IncidentExport,
  PreQuestionData,
} from 'app/shared/models';
import { Constants } from 'app/shared/config/constants';
import { UserService } from 'app/shared/services/user.service';
import { AuthService } from 'app/shared/services/auth.service';
import { map } from 'rxjs/operators';
import { DisseminationHistory } from "app/shared/models/dissemination-history.model";

@Injectable ( {
  providedIn: 'root',
} )
export class IncidentService {

  readonly url = Constants.incidentsServiceUrl;
  readonly incidentMetadataUrl = Constants.incidentsMetadataUrl;
  readonly preQuestionUrl = Constants.preQuestionDataUrl;
  readonly ruleExecuteUrl = {
    branch: Constants.branchRuleExecuteUrl,
    contact: Constants.contactRuleExecuteUrl,
    vehicle: Constants.vehicleRuleExecuteUrl,
    product: Constants.productRuleExecuteUrl,
  };
  readonly incidentsDuplicateCheckUrl = Constants.incidentsDuplicateCheckUrl;

  constructor ( private http: HttpClient, private userService: UserService, private authService: AuthService ) {
  }

  /**
   * Provides advanced incident search call
   * @param clientNumber
   * @param criteria
   * @param businessUnitId
   * @param includeSubUnits
   * @param page
   * @param pageSize
   *
   * @returns Observable of array of incidents
   */
  search (
    clientNumber: string,
    criteria: any,
    businessUnitId: string,
    includeSubUnits = true,
    pageNumber: number, pageSize: number,
    sortDirection = 'ASC',
    sortField?: string,
    includeDeleted?: boolean ): Observable<any> {
    let params = new HttpParams ()
      .set ( 'pageNumber', `${ pageNumber }` )
      .set ( 'pageSize', `${ pageSize }` )
      .set ( 'clientNumber', `${ clientNumber }` )
      .set ( 'includeLocationDescendants', `${ includeSubUnits }` );

    if ( businessUnitId ) {
      params = params.append ( 'locationId', businessUnitId );
    }

    if ( sortField ) {
      params = params.append ( 'sortField', sortField );
      params = params.append ( 'sortDirection', sortDirection );
    }

    if ( includeDeleted ) {
      params = params.append ( 'includeDeleted', 'true' );
    }

    for ( const key in criteria ) {
      if ( criteria[ key ] !== undefined && criteria[ key ] !== null && criteria[ key ] !== '' ) {
        params = params.append ( `${ key }`, `${ criteria[ key ] }` );
      }
    }
    return this.http.get<any> ( this.url, { params } );
  }

  findByClaimNumber ( claimNumber: string ): Observable<Incident | null> {
    const clientNumber = this.userService.currentClient?.clientNumber;

    if ( !clientNumber || !claimNumber ) {
      return of ( null );
    }

    return this.search ( clientNumber, { claimNumber }, null, true, 1, 1 )
      .pipe ( map ( response => response?.records?.[ 0 ] || null ) );
  }

  /**
   * @returns Observable of Incident
   */
  getDefault (): Observable<Incident> {
    return this.http.get<Incident> ( `${ this.url }/default` );
  }

  /**
   * @param incident
   * @param insuredLocationId
   * @param employerLocationId
   * @param policyId
   * @param employeeId
   * @param employeeLookupId
   * @param emailDirectMessageId
   * @returns Observable of Incident
   */
  create (
    incident: Incident,
    insuredLocationId?: string,
    employerLocationId?: string,
    policyId?: string,
    employeeId?: string,
    employeeLookupId?: string,
    emailDirectMessageId?: string,
    vehicleId?: string,
    insuredVehicleDriverLastName?: string,
    ukRegistrationNumber?: string,
    otherDriverLastName?: string,
    otherVehiclePlateNumber?: string,
  ): Observable<any> {

    let params = new HttpParams ();
    if ( insuredLocationId ) {
      params = params.append ( 'insuredLocationId', insuredLocationId );
    }
    if ( employerLocationId ) {
      params = params.append ( 'employerLocationId', employerLocationId );
    }
    if ( policyId ) {
      params = params.append ( 'policyId', policyId );
    }
    if ( employeeId ) {
      params = params.append ( 'employeeId', employeeId );
    } else if ( employeeLookupId ) {
      params = params.append ( 'employeeLookupId', employeeLookupId );
    }
    if ( incident?.questionnaireName ) {
      params = params.append ( 'questionnaireName', incident.questionnaireName );
      delete incident.questionnaireId;
      delete incident.questionnaireName;
    }
    if ( emailDirectMessageId ) {
      params = params.append ( 'emailDirectMessageId', emailDirectMessageId );
    }
    if ( vehicleId ) {
      params = params.append ( 'vehicleId', vehicleId );
    }
    if ( insuredVehicleDriverLastName ) {
      params = params.append ( 'insuredVehicleDriverLastName', insuredVehicleDriverLastName );
    }
    if ( ukRegistrationNumber ) {
      params = params.append ( 'ukRegistrationNumber', ukRegistrationNumber );
    }
    if ( otherDriverLastName ) {
      params = params.append ( 'otherDriverLastName', otherDriverLastName );
    }
    if ( otherVehiclePlateNumber ) {
      params = params.append ( 'otherVehiclePlateNumber', otherVehiclePlateNumber );
    }
    return this.http.post<Incident> ( `${ this.url }`, incident, { params } );
  }

  directCreate ( queryParams: any, ): Observable<Incident> {
    const params = new HttpParams ().appendAll ( queryParams );
    return this.http.post<Incident> ( `${ this.url }`, {}, { params } );
  }

  /**
   * @param id
   * @returns Observable of Incident
   */
  getWizardPage ( id: number | string, page: number ): Observable<any> {
    return this.http.get<any> ( `${ this.url }/${ id }/wizard/${ page }` );
  }

  /**
   * @param id
   * @param data
   * @param page
   * @returns Observable of any
   */
  updatePage ( id: number, data: any, page: number ): Observable<any> {
    const params = new HttpParams ()
      .append ( 'requestedPageIndex', `${ page }` );
    return this.http.put<any> ( `${ this.url }/${ id }`, data, { params } );
  }

  getSummaryPage ( id: number | string ) {
    return this.http.get<any> ( `${ this.url }/${ id }/summary` );
  }

  finishIncident ( id: number | string ) {
    return this.http.put<any> ( `${ this.url }/${ id }/finish`, {} );
  }

  delete ( id: number | string ) {
    return this.http.delete<any> ( `${ this.url }/${ id }`, {} );
  }

  undelete ( id: number | string ) {
    return this.http.post<any> ( `${ this.url }/${ id }?action=UNDELETE`, {} );
  }

  printDocument ( id: string, formTemplateId: string ) {
    return this.http.request ( 'GET', `${ this.url }/${ id }/printable-document?formTemplateId=${ formTemplateId }`, {
      observe: 'response',
      responseType: 'blob',
    } );
  }

  getPrintableDocuments ( id: string ): Observable<AvailableDocument[]> {
    const url = `${ this.url }/${ id }/available-documents`;
    return this.http.get <AvailableDocument[]> ( url );
  }

  setForReview ( id: string ): Observable<Incident> {
    return this.http.put<Incident> ( `${ this.url }/${ id }/review`, {} );
  }

  getPreQuestionData (
    clientNumber: string,
    activeDate: string,
    locationId: string,
    lineOfBusiness: string,
    jurisdiction: string ): Observable<PreQuestionData> {
    let params = new HttpParams ().append ( 'activeDate', activeDate )
      .append ( 'lineOfBusiness', lineOfBusiness )
      .append ( 'clientNumber', clientNumber );
    if ( jurisdiction ) {
      params = params.append ( 'jurisdiction', jurisdiction || null );
    }
    if ( locationId ) {
      params = params.append ( 'locationId', locationId || null );
    }

    return this.http.get<PreQuestionData> ( this.preQuestionUrl, { params } );
  }

  executeRule ( incidentId: string, type: string, pageIndex: number, idObject: any, questionKey: string ) {
    const params = new HttpParams ()
      .appendAll ( {
        ...idObject,
        incidentId,
        pageIndex,
        questionKey,
      } );
    return this.http.get ( this.ruleExecuteUrl[ type ], { params } );
  }

  duplicateCheck (
    lineOfBusiness: string,
    dateOfLoss: string,
    jurisdiction: string,
    locationId: string,
    employeeId?: string,
    employeeLookupId?: string,
    insuredVehicleDriverLastName?: string,
    ukRegistrationNumber?: string,
    otherDriverLastName?: string,
    otherVehiclePlateNumber?: string,
  ): Observable<any> {

    let params = new HttpParams ()
      .append ( 'lineOfBusiness', lineOfBusiness )
      .append ( 'dateOfLoss', dateOfLoss )
      .append ( 'locationId', locationId );

    if ( jurisdiction?.trim ().length ) {
      params = params.append ( 'jurisdiction', jurisdiction );
    }

    if ( employeeId ) {
      params = params.append ( 'employeeId', employeeId );
    }
    if ( employeeLookupId ) {
      params = params.append ( 'employeeLookupId', employeeLookupId );
    }
    if ( insuredVehicleDriverLastName ) {
      params = params.append ( 'insuredVehicleDriverLastName', insuredVehicleDriverLastName );
    }
    if ( ukRegistrationNumber ) {
      params = params.append ( 'ukRegistrationNumber', ukRegistrationNumber );
    }
    if ( otherDriverLastName ) {
      params = params.append ( 'otherDriverLastName', otherDriverLastName );
    }
    if ( otherVehiclePlateNumber ) {
      params = params.append ( 'otherVehiclePlateNumber', otherVehiclePlateNumber );
    }
    return this.http.get<any> ( `${ this.incidentsDuplicateCheckUrl }`, { params } );
  }

  getConfirmationEmails ( incidentId: string, disseminationType?: string ): Observable<string[]> {
    let params = new HttpParams ();
    if ( disseminationType ) {
      params = params.append ( 'disseminationType', disseminationType );
    }

    const url = `${ this.url }/${ incidentId }/confirmation-emails`;
    return this.http.get<string[]> ( url, { params } );
  }

  resendConfirmationEmail ( incidentId: string, disseminationType?: string ): Observable<any> {
    const url = `${ this.url }/${ incidentId }/confirmation-emails/send${ disseminationType ? '?disseminationType=' + disseminationType : '' }`;
    return this.http.post<any> ( url, null );
  }

  getIncidentMetaData ( incidentId: string ): Observable<any> {
    const url = `${ this.url }/${ incidentId }/metadata`;
    return this.http.get<any> ( url );
  }

  getIncidentMetaDataByClaimNumber ( clientNumber: string, claimNumber: string ): Observable<any> {
    const params = new HttpParams ()
      .append ( 'clientNumber', clientNumber )
      .append ( 'claimNumber', claimNumber );

    return this.http.get<any> ( this.incidentMetadataUrl, { params } );
  }

  updateIncidentProperties ( incidentProperties: any ): Observable<any> {
    const url = `${ this.url }/${ incidentProperties.incidentId }/metadata`;
    return this.http.put<any> ( url, incidentProperties );
  }

  getIncidentAnswers ( incidentId: string ): Observable<any> {
    const url = `${ this.url }/${ incidentId }/answers`;
    return this.http.get<any> ( url ).pipe ( map ( answers => {
      const questionNames = new Set ( answers.map ( answer => answer.questionName ) );
      const grouped = Array.from ( questionNames ).map ( questionName => {
        const answersForQuestion = answers
          .filter ( answer => answer.questionName === questionName )
          .sort ( ( a, b ) => a.sequence - b.sequence );
        return {
          questionName,
          answers: answersForQuestion,
        };
      } ).sort ( ( a, b ) => ( a.questionName as string ).localeCompare ( b.questionName as string ) );
      return grouped;
    } ) );
  }

  getIncidentAnswersAuditHistory ( incidentId: string, pageNumber = 1, pageSize = 50 ): Observable<any> {
    const url = `${ this.url }/${ incidentId }/answers/audit-history`;
    const params = new HttpParams ().appendAll ( {
      pageNumber,
      pageSize,
    } );
    return this.http.get<any> ( url, { params } ).pipe ( map ( history => {
      return history;
    } ) );
  }

  getExports ( incidentId: string ): Observable<IncidentExport[]> {
    const url = `${ this.url }/${ incidentId }/exports`;
    return this.http.get<IncidentExport[]> ( url ).pipe ( map ( incidentExports => {
      return incidentExports.sort ( ( a, b ) => {
        return a.exportType.displayIndex - b.exportType.displayIndex;
      } );
    } ) );
  }

  getExportTypes ( incidentId: string ): Observable<any[]> {
    const url = `${ this.url }/${ incidentId }/exports/export-types`;
    return this.http.get<any[]> ( url ).pipe ( map ( types => {
      return types.sort ( ( a, b ) => {
        return a.displayIndex - b.displayIndex;
      } );
    } ) );
  }

  resendExport ( incidentId: string, exportIndicator: any, attachmentsOnly = false ): Observable<IncidentExport[]> {
    const url = `${ this.url }/${ incidentId }/exports/${ exportIndicator.exportType }`;
    let params = new HttpParams ();
    if ( attachmentsOnly ) {
      params = params.append ( 'attachmentsOnly', 'true' );
    }
    return this.http.post<IncidentExport[]> ( url, exportIndicator, { params } );
  }

  resubmitAck ( incidentId: string, exportId: string, reason: string ): Observable<IncidentExport[]> {
    const url = `${ this.url }/${ incidentId }/exports/${ exportId }/ack?reason=${ reason }`;
    return this.http.post<IncidentExport[]> ( url, {} );
  }

  deleteExport ( incidentId: string, exportType: string, exportId: string ): Observable<any> {
    return this.http.delete<any> ( `${ this.url }/${ incidentId }/exports/${ exportType }/${ exportId }` );
  }

  releaseExport ( incidentId: string, incidentExport: IncidentExport ): Observable<any> {
    return this.http.put<any> ( `${ this.url }/${ incidentId }/exports/${ incidentExport.exportType.key }?release=true`, incidentExport );
  }

  reassignReferenceNumber ( incidentId: string, force = false ): Observable<any> {
    const url = `${ this.url }/${ incidentId }/reassign-reference-number`;
    let params = new HttpParams ();
    if ( force ) {
      params = params.append ( 'force', 'true' );
    }
    return this.http.put<any> ( url, {}, { params } );
  }

  cloneIncident ( cloneIncidentId: string, cloneLineOfBusiness: string, cloneQuestionnaireId: string ): Observable<any> {
    const params = new HttpParams ()
      .appendAll ( {
        cloneIncidentId,
        cloneLineOfBusiness,
        cloneQuestionnaireId,
      } );
    return this.http.post<any> ( this.url, {}, { params } );
  }

  refinishIncident ( incidentId: string ): Observable<any> {
    return this.http.post<any> ( `${ this.url }/${ incidentId }?action=REFINISH`, {} );
  }

  /**
   * Fetches the dissemination history records for the incident
   * @param incidentId
   * @returns Observable of dissemination history records
   */
  getDisseminationHistories ( incidentId: string ): Observable<DisseminationHistory[]> {
    const url = `${ this.url }/${ incidentId }/dissemination-histories`;
    return this.http.get<DisseminationHistory[]> ( url );
  }
}
